import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/esm/Add';
import AssignmentLate from '@mui/icons-material/esm/AssignmentLate';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import Delete from '@mui/icons-material/esm/Delete';
import Edit from '@mui/icons-material/esm/Edit';
import EventAvailable from '@mui/icons-material/esm/EventAvailable';
import NotificationsActive from '@mui/icons-material/esm/NotificationsActive';
import PlaylistAdd from '@mui/icons-material/esm/PlaylistAdd';
import Refresh from '@mui/icons-material/esm/Refresh';

import { KPICard, DataTable, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import permitsService, {
  PermitConditionStatus,
  PermitPayload,
  PermitRenewalStatus,
  PermitRow,
  PermitStatus,
  PermitType,
} from '../../services/api/permitsService';

const PERMIT_TYPES: PermitType[] = [
  'nepa_building',
  'nepa_environmental',
  'fire_safety',
  'occupancy',
  'demolition',
  'subdivision',
  'mining',
  'utility_connection',
  'other',
];
const PERMIT_STATUSES: PermitStatus[] = ['draft', 'submitted', 'under_review', 'approved', 'active', 'renewal_pending', 'expired', 'closed', 'rejected'];
const CONDITION_STATUSES: PermitConditionStatus[] = ['open', 'in_progress', 'satisfied', 'waived'];
const RENEWAL_STATUSES: PermitRenewalStatus[] = ['planned', 'submitted', 'approved', 'rejected', 'cancelled'];
const PERMIT_TYPE_LABELS: Record<PermitType, string> = {
  nepa_building: 'NEPA building',
  nepa_environmental: 'NEPA environmental',
  fire_safety: 'Fire safety',
  occupancy: 'Occupancy',
  demolition: 'Demolition',
  subdivision: 'Subdivision',
  mining: 'Mining',
  utility_connection: 'Utility connection',
  other: 'Other',
};

const emptyPermit = (projectId: string | null): PermitPayload => ({
  projectId: projectId ?? '',
  permitNumber: `PER-${Date.now().toString().slice(-6)}`,
  title: '',
  permitType: 'nepa_building',
  issuingAuthority: '',
  status: 'draft',
  applicationDate: '',
  issueDate: '',
  expiryDate: '',
});

const statusColor = (status: PermitStatus): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  if (status === 'active' || status === 'approved') return 'success';
  if (status === 'submitted' || status === 'under_review' || status === 'renewal_pending') return 'warning';
  if (status === 'expired' || status === 'rejected') return 'error';
  if (status === 'closed') return 'default';
  return 'info';
};

const daysUntil = (date?: string | null): number | null => {
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
};

const dateInput = (value?: string | null) => value ? value.slice(0, 10) : '';

export default function PermitsPage() {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [permits, setPermits] = useState<PermitRow[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPermit, setEditingPermit] = useState<PermitRow | null>(null);
  const [permitForm, setPermitForm] = useState<PermitPayload>(emptyPermit(null));
  const [conditionPermit, setConditionPermit] = useState<PermitRow | null>(null);
  const [conditionCode, setConditionCode] = useState('');
  const [conditionDescription, setConditionDescription] = useState('');
  const [conditionDueDate, setConditionDueDate] = useState('');
  const [conditionStatus, setConditionStatus] = useState<PermitConditionStatus>('open');
  const [renewalPermit, setRenewalPermit] = useState<PermitRow | null>(null);
  const [renewalNumber, setRenewalNumber] = useState('');
  const [renewalStatus, setRenewalStatus] = useState<PermitRenewalStatus>('planned');
  const [renewalApplicationDate, setRenewalApplicationDate] = useState('');
  const [renewalExpiryDate, setRenewalExpiryDate] = useState('');
  const [renewalNotes, setRenewalNotes] = useState('');

  const fetchData = async () => {
    if (!activeProjectId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await permitsService.list({ projectId: activeProjectId });
      setPermits(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load permits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId]);

  const summary = useMemo(() => {
    const active = permits.filter((permit) => permit.status === 'active' || permit.status === 'approved').length;
    const renewalPending = permits.filter((permit) => permit.status === 'renewal_pending').length;
    const expiring = permits.filter((permit) => {
      const days = daysUntil(permit.expiryDate);
      return permit.status !== 'closed' && days !== null && days >= 0 && days <= 30;
    }).length;
    const openConditions = permits.reduce((count, permit) => (
      count + (permit.conditions ?? []).filter((condition) => condition.status === 'open' || condition.status === 'in_progress').length
    ), 0);
    return { total: permits.length, active, renewalPending, expiring, openConditions };
  }, [permits]);

  const openCreate = () => {
    setEditingPermit(null);
    setPermitForm(emptyPermit(activeProjectId));
    setFormOpen(true);
  };

  const openEdit = (permit: PermitRow) => {
    setEditingPermit(permit);
    setPermitForm({
      projectId: permit.projectId,
      permitNumber: permit.permitNumber,
      title: permit.title,
      permitType: permit.permitType,
      issuingAuthority: permit.issuingAuthority,
      status: permit.status,
      applicationDate: dateInput(permit.applicationDate),
      issueDate: dateInput(permit.issueDate),
      expiryDate: dateInput(permit.expiryDate),
      responsibleUserId: permit.responsibleUserId,
      documentId: permit.documentId,
    });
    setFormOpen(true);
  };

  const savePermit = async () => {
    try {
      if (!permitForm.projectId) {
        setError('Select a project before creating a permit.');
        return;
      }
      const payload = {
        ...permitForm,
        applicationDate: permitForm.applicationDate || null,
        issueDate: permitForm.issueDate || null,
        expiryDate: permitForm.expiryDate || null,
      };
      if (editingPermit) {
        await permitsService.update(editingPermit.id, payload);
      } else {
        await permitsService.create(payload);
      }
      setFormOpen(false);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save permit');
    }
  };

  const closePermit = async (permit: PermitRow) => {
    try {
      await permitsService.close(permit.id);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to close permit');
    }
  };

  const runAlerts = async () => {
    try {
      const result = await permitsService.runExpiryAlerts(30);
      setSuccess(`Expiry check scanned ${result.checked} permits and created ${result.alertsCreated} alerts.`);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run expiry alerts');
    }
  };

  const addCondition = async () => {
    if (!conditionPermit || !conditionCode.trim() || !conditionDescription.trim()) return;
    try {
      await permitsService.addCondition(conditionPermit.id, {
        conditionCode,
        description: conditionDescription,
        dueDate: conditionDueDate || null,
        status: conditionStatus,
      });
      setConditionCode('');
      setConditionDescription('');
      setConditionDueDate('');
      setConditionStatus('open');
      await fetchData();
      const refreshed = await permitsService.list({ projectId: activeProjectId ?? undefined });
      setConditionPermit(refreshed.find((permit) => permit.id === conditionPermit.id) ?? conditionPermit);
      setPermits(refreshed);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save condition');
    }
  };

  const updateCondition = async (conditionId: string, status: PermitConditionStatus) => {
    try {
      await permitsService.updateCondition(conditionId, { status });
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update condition');
    }
  };

  const deleteCondition = async (conditionId: string) => {
    try {
      await permitsService.deleteCondition(conditionId);
      await fetchData();
      if (conditionPermit) {
        setConditionPermit({
          ...conditionPermit,
          conditions: (conditionPermit.conditions ?? []).filter((condition) => condition.id !== conditionId),
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete condition');
    }
  };

  const addRenewal = async () => {
    if (!renewalPermit || !renewalNumber.trim()) return;
    try {
      await permitsService.addRenewal(renewalPermit.id, {
        renewalNumber,
        status: renewalStatus,
        applicationDate: renewalApplicationDate || null,
        newExpiryDate: renewalExpiryDate || null,
        notes: renewalNotes || null,
      });
      setRenewalNumber('');
      setRenewalStatus('planned');
      setRenewalApplicationDate('');
      setRenewalExpiryDate('');
      setRenewalNotes('');
      const refreshed = await permitsService.list({ projectId: activeProjectId ?? undefined });
      setPermits(refreshed);
      setRenewalPermit(refreshed.find((permit) => permit.id === renewalPermit.id) ?? renewalPermit);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save renewal');
    }
  };

  const updateRenewal = async (renewalId: string, status: PermitRenewalStatus) => {
    try {
      await permitsService.updateRenewal(renewalId, { status });
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update renewal');
    }
  };

  const deleteRenewal = async (renewalId: string) => {
    try {
      await permitsService.deleteRenewal(renewalId);
      await fetchData();
      if (renewalPermit) {
        setRenewalPermit({
          ...renewalPermit,
          renewals: (renewalPermit.renewals ?? []).filter((renewal) => renewal.id !== renewalId),
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete renewal');
    }
  };

  const columns: Column<PermitRow>[] = [
    { id: 'permitNumber', label: 'Permit No.', minWidth: 120 },
    { id: 'title', label: 'Title', minWidth: 220 },
    {
      id: 'permitType',
      label: 'Type',
      minWidth: 120,
      format: (value: PermitType) => <Chip size="small" label={PERMIT_TYPE_LABELS[value] ?? value} />,
    },
    { id: 'issuingAuthority', label: 'Authority', minWidth: 180 },
    {
      id: 'status',
      label: 'Status',
      minWidth: 140,
      format: (value: PermitStatus) => <Chip size="small" color={statusColor(value)} label={value.replace('_', ' ')} />,
    },
    {
      id: 'expiryDate',
      label: 'Expiry',
      minWidth: 130,
      format: (value: string | null) => {
        const days = daysUntil(value);
        return value ? `${value.slice(0, 10)}${days !== null && days <= 30 && days >= 0 ? ` (${days}d)` : ''}` : 'None';
      },
    },
    {
      id: 'conditions',
      label: 'Open Conditions',
      minWidth: 130,
      align: 'center',
      format: (_value, row) => (row.conditions ?? []).filter((condition) => condition.status === 'open' || condition.status === 'in_progress').length,
    },
    {
      id: 'actions',
      label: '',
      minWidth: 190,
      sortable: false,
      format: (_value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit permit">
            <IconButton size="small" onClick={() => openEdit(row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Conditions">
            <IconButton size="small" onClick={() => setConditionPermit(row)}>
              <PlaylistAdd fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Renewals">
            <IconButton size="small" onClick={() => setRenewalPermit(row)}>
              <EventAvailable fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.status !== 'closed' && (
            <Tooltip title="Close permit">
              <IconButton size="small" onClick={() => closePermit(row)}>
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (!activeProjectId) {
    return (
      <Box>
        <PageHeader title="Permits" />
        <Alert severity="info">Select a project to view permits.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Permits"
        subtitle={`${summary.total} permits tracked`}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Run expiry alerts">
              <IconButton onClick={runAlerts}>
                <NotificationsActive />
              </IconButton>
            </Tooltip>
            <Button startIcon={<Add />} variant="contained" onClick={openCreate}>
              New Permit
            </Button>
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Active" value={`${summary.active}`} icon={<EventAvailable />} color="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Expiring 30d" value={`${summary.expiring}`} icon={<AssignmentLate />} color={summary.expiring > 0 ? 'warning' : 'success'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Renewal Pending" value={`${summary.renewalPending}`} icon={<Refresh />} color={summary.renewalPending > 0 ? 'warning' : 'info'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Open Conditions" value={`${summary.openConditions}`} icon={<PlaylistAdd />} color={summary.openConditions > 0 ? 'primary' : 'success'} />
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <DataTable columns={columns} data={permits} searchable pagination defaultRowsPerPage={10} />
        </Box>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPermit ? 'Edit Permit' : 'New Permit'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, pt: 2 }}>
          <TextField label="Permit No." value={permitForm.permitNumber} onChange={(e) => setPermitForm({ ...permitForm, permitNumber: e.target.value })} />
          <TextField label="Title" value={permitForm.title} onChange={(e) => setPermitForm({ ...permitForm, title: e.target.value })} />
          <TextField select label="Type" value={permitForm.permitType} onChange={(e) => setPermitForm({ ...permitForm, permitType: e.target.value as PermitType })}>
            {PERMIT_TYPES.map((type) => <MenuItem key={type} value={type}>{PERMIT_TYPE_LABELS[type]}</MenuItem>)}
          </TextField>
          <TextField select label="Status" value={permitForm.status ?? 'draft'} onChange={(e) => setPermitForm({ ...permitForm, status: e.target.value as PermitStatus })}>
            {PERMIT_STATUSES.map((status) => <MenuItem key={status} value={status}>{status.replace('_', ' ')}</MenuItem>)}
          </TextField>
          <TextField label="Issuing Authority" value={permitForm.issuingAuthority} onChange={(e) => setPermitForm({ ...permitForm, issuingAuthority: e.target.value })} />
          <TextField type="date" label="Application" InputLabelProps={{ shrink: true }} value={dateInput(permitForm.applicationDate)} onChange={(e) => setPermitForm({ ...permitForm, applicationDate: e.target.value })} />
          <TextField type="date" label="Issue" InputLabelProps={{ shrink: true }} value={dateInput(permitForm.issueDate)} onChange={(e) => setPermitForm({ ...permitForm, issueDate: e.target.value })} />
          <TextField type="date" label="Expiry" InputLabelProps={{ shrink: true }} value={dateInput(permitForm.expiryDate)} onChange={(e) => setPermitForm({ ...permitForm, expiryDate: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePermit}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(conditionPermit)} onClose={() => setConditionPermit(null)} maxWidth="md" fullWidth>
        <DialogTitle>Permit Conditions</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {conditionPermit && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{conditionPermit.permitNumber} - {conditionPermit.title}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '130px 1fr 150px 150px auto' }, gap: 1.5, mb: 2 }}>
                <TextField label="Code" value={conditionCode} onChange={(e) => setConditionCode(e.target.value)} />
                <TextField label="Condition" value={conditionDescription} onChange={(e) => setConditionDescription(e.target.value)} />
                <TextField type="date" label="Due" InputLabelProps={{ shrink: true }} value={conditionDueDate} onChange={(e) => setConditionDueDate(e.target.value)} />
                <TextField select label="Status" value={conditionStatus} onChange={(e) => setConditionStatus(e.target.value as PermitConditionStatus)}>
                  {CONDITION_STATUSES.map((status) => <MenuItem key={status} value={status}>{status.replace('_', ' ')}</MenuItem>)}
                </TextField>
                <Button variant="contained" onClick={addCondition} startIcon={<Add />}>Add</Button>
              </Box>
              <Box sx={{ display: 'grid', gap: 1 }}>
                {(conditionPermit.conditions ?? []).map((condition) => (
                  <Box key={condition.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '120px 1fr 140px 160px 44px' }, gap: 1, alignItems: 'center', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">{condition.conditionCode}</Typography>
                    <Typography variant="body2">{condition.description}</Typography>
                    <Typography variant="body2" color="text.secondary">{condition.dueDate ?? 'No due date'}</Typography>
                    <TextField select size="small" value={condition.status} onChange={(e) => updateCondition(condition.id, e.target.value as PermitConditionStatus)}>
                      {CONDITION_STATUSES.map((status) => <MenuItem key={status} value={status}>{status.replace('_', ' ')}</MenuItem>)}
                    </TextField>
                    <Tooltip title="Delete condition">
                      <IconButton size="small" onClick={() => deleteCondition(condition.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
                {(conditionPermit.conditions ?? []).length === 0 && <Alert severity="info">No permit conditions recorded.</Alert>}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConditionPermit(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(renewalPermit)} onClose={() => setRenewalPermit(null)} maxWidth="md" fullWidth>
        <DialogTitle>Permit Renewals</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {renewalPermit && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{renewalPermit.permitNumber} - {renewalPermit.title}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '140px 150px 150px 150px 1fr auto' }, gap: 1.5, mb: 2 }}>
                <TextField label="Renewal No." value={renewalNumber} onChange={(e) => setRenewalNumber(e.target.value)} />
                <TextField select label="Status" value={renewalStatus} onChange={(e) => setRenewalStatus(e.target.value as PermitRenewalStatus)}>
                  {RENEWAL_STATUSES.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                </TextField>
                <TextField type="date" label="Application" InputLabelProps={{ shrink: true }} value={renewalApplicationDate} onChange={(e) => setRenewalApplicationDate(e.target.value)} />
                <TextField type="date" label="New Expiry" InputLabelProps={{ shrink: true }} value={renewalExpiryDate} onChange={(e) => setRenewalExpiryDate(e.target.value)} />
                <TextField label="Notes" value={renewalNotes} onChange={(e) => setRenewalNotes(e.target.value)} />
                <Button variant="contained" onClick={addRenewal} startIcon={<Add />}>Add</Button>
              </Box>
              <Box sx={{ display: 'grid', gap: 1 }}>
                {(renewalPermit.renewals ?? []).map((renewal) => (
                  <Box key={renewal.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '140px 150px 150px 1fr 44px' }, gap: 1, alignItems: 'center', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">{renewal.renewalNumber}</Typography>
                    <TextField select size="small" value={renewal.status} onChange={(e) => updateRenewal(renewal.id, e.target.value as PermitRenewalStatus)}>
                      {RENEWAL_STATUSES.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                    </TextField>
                    <Typography variant="body2" color="text.secondary">{renewal.newExpiryDate ?? 'No expiry'}</Typography>
                    <Typography variant="body2">{renewal.notes ?? ''}</Typography>
                    <Tooltip title="Delete renewal">
                      <IconButton size="small" onClick={() => deleteRenewal(renewal.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
                {(renewalPermit.renewals ?? []).length === 0 && <Alert severity="info">No renewals recorded.</Alert>}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewalPermit(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
